import { useMutation } from '@tanstack/react-query';

import { useAuthContext } from '~/shared/context/useAuthContext';
import { useXFetch } from '~/shared/utils/useXFetch';
import { useOrganizationStatus } from '~/shared/utils/organizationUtils';

import type { CreateRuleExclusionResponse } from '~/shared/types/api';

import { buildVolumeExclusionsUrl } from './buildVolumeExclusionsUrl';
import {
  assertVolumeExclusionAccountId,
  handleVolumeExclusionError,
} from './volumeExclusionErrors';

export interface VolumeExclusionParams {
  volumeId: string;
  accountId?: string;
}

export type CreateExceptionParams = VolumeExclusionParams;
export type RemoveExceptionParams = VolumeExclusionParams;

type ExclusionAction = 'create' | 'remove';

function useVolumeExclusionMutation<TResponse>(
  method: 'POST' | 'DELETE',
  action: ExclusionAction
) {
  const api = useXFetch();
  const { accounts, activeTenantId } = useAuthContext();
  const { isOrganizationAccount } = useOrganizationStatus(accounts, activeTenantId);

  return useMutation({
    mutationFn: async ({ volumeId, accountId }: VolumeExclusionParams): Promise<TResponse> => {
      assertVolumeExclusionAccountId(accountId, isOrganizationAccount);

      const response = await api(buildVolumeExclusionsUrl(volumeId, accountId), {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response as TResponse;
    },
    onError: (error) => {
      handleVolumeExclusionError(error, action);
    },
  });
}

export function useCreateException() {
  return useVolumeExclusionMutation<CreateRuleExclusionResponse>('POST', 'create');
}

export function useRemoveException() {
  return useVolumeExclusionMutation<unknown>('DELETE', 'remove');
}
